import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import moment from 'moment';

import {
    Button,
    IconButton,
    Modal,
    Tooltip,
    Pill
} from '@gpstrackit/gtc-react-controls';
import { NotificationContainer } from "Components/Notifications";
import { NotificationManager } from 'Components/Notifications';
import { Grid, Typography, Icon } from '@mui/material';

//Actions
import {
    openCloseModalMaintenanceServicesToComplete,
    getMaintenanceServices,
    reset,
} from './redux/slice';

import { getMaintenanceStatusColor, getMaintenanceBar } from '../../../../core/hooks/utils';
import { convertToKPH } from 'Core/data/Helpers';

import {
    openModalCompleteMaintenance
} from 'Redux/actions';

//Styles
import './styles.scss';

const MaintenanceServicesToComplete = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();

    const user = useSelector(state => state.securityRedux.user);

    const open = useSelector(state => state.maintenanceServicesToCompleteRedux.open);
    const loading = useSelector(state => state.maintenanceServicesToCompleteRedux.loading);
    const vehicle = useSelector(state => state.maintenanceServicesToCompleteRedux.vehicle);
    const services = useSelector(state => state.maintenanceServicesToCompleteRedux.services);
    const error = useSelector(state => state.maintenanceServicesToCompleteRedux.error);

    useEffect(() => {
        if (vehicle && open) {
            dispatch(getMaintenanceServices({ unitId: vehicle?.id }));
        }
    }, [vehicle, open]);

    useEffect(() => {
        if (services?.length && open) {
            if (services?.length < 2) {
                dispatch(openModalCompleteMaintenance({ open: true, serviceUnitId: services[0]?.id }));
                dispatch(reset(null));
            }
        }
    }, [services?.length, open]);

    useEffect(() => {
        if (error) {
            NotificationManager.error(
                error?.error || messages['errorMessage'],
                messages['error'],
                3000,
                null,
                null,
                'error',
            );

            dispatch(reset({ error: null }));
        }
    }, [error]);

    const handleClose = () => {
        dispatch(openCloseModalMaintenanceServicesToComplete({ open: false }));
    }

    const getOdometerBar = (service) => {
        if (!service?.service?.miles || !vehicle) {
            return <span />;
        }

        if (service?.nextServiceOdometer && !parseFloat(vehicle?.ecuEngineOdometer) && !parseFloat(vehicle?.engineOdometer) && !parseFloat(vehicle?.displayOdometer)) {
            return (
                <Tooltip title={messages['vehicleDetailView_warningNotInfoOdometer']} >
                    <Icon style={{ cursor: "pointer", color: '#ffc107' }}>warning</Icon>
                </Tooltip>
            );
        }

        const lastOdometer = user?.isKph ? convertToKPH(service.lastMaintenanceOdometer) : service.lastMaintenanceOdometer;
        const nextOdometer = user?.isKph ? convertToKPH(service.nextServiceOdometer) : service.nextServiceOdometer;
        const currentOdometer = user?.isKph ? convertToKPH(vehicle?.displayOdometer || 0) : (vehicle?.displayOdometer || 0);
        const unitMeasure = user?.isKph ? ` ${messages['kilometers']}` : ` ${messages['miles']}`;

        return getMaintenanceBar("odometer", service.status, lastOdometer, nextOdometer, currentOdometer, messages, null, { ...service, ...vehicle }, user, unitMeasure);
    }

    const getTimeBar = (service) => {
        if (!service?.service?.days || !service.nextServiceDate) {
            return <span />;
        }

        if (service?.service?.days && !service?.nextServiceDate) {
            return (<Tooltip title={`${messages['vehicleDetailView_warningNotNextDate']}`} >
                <Icon style={{ cursor: "pointer", color: '#ffc107' }}>warning</Icon>
            </Tooltip>)
        }

        return getMaintenanceBar("time", service.status, service.lastMaintenanceDate, service.nextServiceDate, moment(), messages, null, { ...service, ...vehicle });
    }

    const getEngineHoursBar = (service) => {
        if (!service?.service?.engineHours || !vehicle) {
            return <span />;
        }

        if (!parseInt(vehicle?.engineHours) && !parseInt(vehicle?.ecuEngineHours) && service?.nextServiceEngineHours && !parseInt(vehicle?.displayHours)) {
            return (
                <Tooltip title={messages['vehicleDetailView_warningNotInfoEngineHour']} >
                    <Icon style={{ cursor: "pointer", color: '#ffc107' }}>warning</Icon>
                </Tooltip>
            );
        }

        return getMaintenanceBar("engineHours", service.status, service.lastMaintenanceEngineHours, service.nextServiceEngineHours, vehicle?.displayHours || 0, messages, null, { ...service, ...vehicle }, user, ` ${messages['hours']}`)
    }

    return (
        <Modal
            title={`${messages['maintenanceServicesToComplete_title']}: "${vehicle?.label}"`}
            open={open}
            handleClose={handleClose}
            position="right"
            size="small"
            loading={loading}
            style={{ zIndex: 'auto' }}
            footer={(
                <div>
                    <Button
                        onClick={handleClose}
                        variant="secondary"
                        style={{ marginRight: "5px" }}
                    >
                        {messages['cancel']}
                    </Button>
                </div>
            )}
        >
            <div style={{ padding: 15 }}>
                <div style={{ marginBottom: 8 }}>
                    <span>{messages['maintenanceServicesToComplete_selectServiceComplete']}</span>
                </div>

                {
                    services?.map((service, index) => {
                        const { tooltip, color } = getMaintenanceStatusColor(service?.status, messages);
                        return (
                            <Grid
                                key={index}
                                container
                                direction="column"
                                className={`maintenace-item ${service?.status}-maintenance-container`}
                            >
                                <Grid
                                    item
                                    container
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Grid item xs zeroMinWidth>
                                        <Typography noWrap className='maintenance-title'>
                                            {service?.service?.title}
                                        </Typography>

                                        <div>
                                            {getOdometerBar(service)}
                                        </div>

                                        <div>
                                            {getTimeBar(service)}
                                        </div>

                                        <div>
                                            {getEngineHoursBar(service)}
                                        </div>

                                        <div style={{ marginTop: 8 }}>
                                            <Pill style={{ width: 110 }} item={{ label: tooltip, color: color }} />
                                        </div>
                                    </Grid>

                                    <Grid item>
                                        <IconButton
                                            variant="secondary"
                                            size="small"
                                            icon='arrow_forward_ios'
                                            onClick={() => {
                                                dispatch(openModalCompleteMaintenance({ open: true, serviceUnitId: service?.id }));
                                                dispatch(reset(null));
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        )
                    })
                }
            </div>
            <NotificationContainer />
        </Modal>
    )
}

export default MaintenanceServicesToComplete;